import * as React from "react";
import { Popover as PopoverPrimitive } from "@base-ui/react/popover";

import { cn } from "../../lib/utils";

const popoverStyles = {
  positioner: "[z-index:var(--z-index-floating)] isolate",
  content:
    "[z-index:var(--z-index-floating)] flex origin-[var(--transform-origin)] flex-col gap-2.5 rounded-lg border border-border bg-floating p-2.5 text-sm leading-sm text-floating-fg shadow-none outline-none [animation-duration:var(--durations-fast)] [animation-timing-function:var(--easings-enter)] [transition-duration:var(--durations-faster)] data-[side=bottom]:[--sho-enter-y:calc(var(--spacing-2)*-1)] data-[side=top]:[--sho-enter-y:var(--spacing-2)] data-[side=left]:[--sho-enter-x:var(--spacing-2)] data-[side=inline-start]:[--sho-enter-x:var(--spacing-2)] data-[side=right]:[--sho-enter-x:calc(var(--spacing-2)*-1)] data-[side=inline-end]:[--sho-enter-x:calc(var(--spacing-2)*-1)] data-[open]:[--sho-enter-scale:0.95] data-[closed]:[--sho-exit-scale:0.95] data-[starting-style]:animate-[sho-enter] data-[ending-style]:animate-[sho-exit]",
};

const popoverContentSizeStyles = {
  sm: "w-56",
  default: "w-72",
  lg: "w-80",
};

function Popover({ ...props }: PopoverPrimitive.Root.Props) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({ ...props }: PopoverPrimitive.Trigger.Props) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
  className,
  align = "center",
  alignOffset = 0,
  side = "bottom",
  sideOffset = 4,
  size = "default",
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<
    PopoverPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset"
  > & {
    size?: "sm" | "default" | "lg";
  }) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className={popoverStyles.positioner}
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={cn(popoverStyles.content, popoverContentSizeStyles[size], className)}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

function PopoverHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="popover-header"
      className={cn(
        "[display:flex] [flex-direction:column] [gap:0.125rem] [font-size:0.875rem] [line-height:1.25rem]",
        className,
      )}
      {...props}
    />
  );
}

function PopoverTitle({ className, ...props }: PopoverPrimitive.Title.Props) {
  return (
    <PopoverPrimitive.Title
      data-slot="popover-title"
      className={cn("[font-weight:500]", className)}
      {...props}
    />
  );
}

function PopoverDescription({
  className,
  ...props
}: PopoverPrimitive.Description.Props) {
  return (
    <PopoverPrimitive.Description
      data-slot="popover-description"
      className={cn("[color:var(--muted-foreground)]", className)}
      {...props}
    />
  );
}

export {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
};
